import { computed, DestroyRef, inject, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStoreFeature, withMethods, withProps, withState, } from '@ngrx/signals';
import { catchError, defer, from, Observable, ObservableInput, ObservedValueOf, tap } from 'rxjs';

export type ActionStatus = 'idle' | 'loading' | 'error' | 'success';
export type ActionTrackerState<T extends string> = {
    actionTrackers: Record<T, ActionStatus>,
};
export type ActionTrackerMethods<T> = {
    getActionTracker(action: T): Signal<ActionStatus>;
    _runActionWithTracking<R>(action: T, actionFn: () => Promise<R>): Observable<R>;
    _trackError(action: T): void;
    _trackIdle(action: T): void;
    _trackLoading(action: T): void;
    _trackSuccess(action: T): void;
};

/**
 * Updates a single action tracker.
 * @param state The parent state.
 * @param action The target action.
 * @param status The status to be applied to the action.
 */
function setActionTracker<T extends string>(
    state: ActionTrackerState<T>,
    action: NoInfer<T>,
    status: ActionStatus,
): ActionTrackerState<T> {
    return {
        actionTrackers: {
            ...state.actionTrackers,
            [action]: status,
        },
    };
}

/**
 * Adds action trackers to a store.
 *
 * Each action tracker holds a `status` (idle, loading, success or error). The feature itself has no opinion about
 * what an "action" is or how it is executed. That has to be decided by the consuming store.
 */
export function withActionTrackers<T extends string>(...actions: T[]) {
    // All actions should be idle by default
    const initialActionStates = Object.fromEntries(
        actions.map((action: T): [ T, ActionStatus ] => [ action, 'idle' ])
    ) as Record<T, ActionStatus>;
    return signalStoreFeature(
        withState<ActionTrackerState<T>>({ actionTrackers: initialActionStates }),
        withProps(() => ({
            _destroyRef: inject(DestroyRef),
        })),
        withMethods((store): ActionTrackerMethods<T> => {
            /**
             * Returns the tracker for a given action.
             * @param action The target action.
             */
            const getActionTracker = (action: T): Signal<ActionStatus> => {
                return computed((): ActionStatus => store.actionTrackers()[action]);
            };

            /**
             * Executes an action function and keeps track of its status.
             * @param action The target action.
             * @param actionFn The action function to be executed.
             */
            const _runActionWithTracking = <R extends ObservableInput<unknown>>(
                action: T,
                actionFn: () => R,
            ): Observable<ObservedValueOf<R>> => {
                return defer((): Observable<ObservedValueOf<R>> => {
                    if (store.actionTrackers()[action] === 'loading') {
                        // No parallel actions allowed
                        throw new Error('Action is still pending!');
                    }
                    _trackLoading(action);
                    return from(actionFn()).pipe(
                        takeUntilDestroyed(store._destroyRef),
                        tap((): void => _trackSuccess(action)),
                        catchError((error: unknown): never => {
                            _trackError(action);
                            throw error;
                        }),
                    );
                });
            };

            /**
             * Marks the given action as errored.
             * @param action The target action.
             */
            const _trackError = (action: T): void => {
                patchState(store, (state: ActionTrackerState<T>): ActionTrackerState<T> =>
                    setActionTracker(
                        state,
                        action,
                        'error',
                    ),
                );
            };

            /**
             * Marks the given action as idling.
             * @param action The target action.
             */
            const _trackIdle = (action: T): void => {
                patchState(store, (state: ActionTrackerState<T>): ActionTrackerState<T> =>
                    setActionTracker(
                        state,
                        action,
                        'idle',
                    ),
                );
            };

            /**
             * Marks the given action as loading.
             * @param action The target action.
             */
            const _trackLoading = (action: T): void => {
                patchState(store, (state: ActionTrackerState<T>): ActionTrackerState<T> =>
                    setActionTracker(
                        state,
                        action,
                        'loading',
                    ),
                );
            };

            /**
             * Marks the given action as successfully completed.
             * @param action The target action.
             */
            const _trackSuccess = (action: T): void => {
                patchState(store, (state: ActionTrackerState<T>): ActionTrackerState<T> =>
                    setActionTracker(
                        state,
                        action,
                        'success',
                    ),
                );
            };

            return {
                getActionTracker,
                _runActionWithTracking,
                _trackError,
                _trackIdle,
                _trackLoading,
                _trackSuccess,
            };
        }),
    );
}